import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppShell } from '@/app/AppShell';
import { HomeOperacional } from '@/app/HomeOperacional';
import { PaginaIngestao } from '@/app/PaginaIngestao';
import { PainelDeExploracao } from '@/app/PainelDeExploracao';
import { useRota } from '@/app/Roteador';
import { IconeDiagnostico } from '@/components/Icones';
import { I18nProvider, useI18n } from '@/i18n/I18nProvider';
import { Login } from '@/features/autenticacao-admin/Login';
import { SessaoAdminProvider, useSessaoAdmin } from '@/features/autenticacao-admin/sessaoAdmin';
import { SessaoProvider } from '@/features/authentication/sessao';
import { ExploracaoProvider, useExploracao } from '@/features/exploracao/contexto';
import { SelecaoProvider, useSelecao } from '@/features/feicao/selecao';
import { Saude } from '@/features/health/Saude';
import { GavetaTecnica } from '@/features/painel-tecnico/GavetaTecnica';
import { InspecaoProvider } from '@/features/painel-tecnico/registro';
import { CamadasNoMapa } from '@/map/CamadasNoMapa';
import { CamadasProvider } from '@/map/camadas';
import { ControlesCartograficos } from '@/map/ControlesCartograficos';
import { LegendaHipsometrica } from '@/map/LegendaHipsometrica';
import { LegendaCartografica } from '@/map/LegendaCartografica';
import { MedicaoCartografica } from '@/map/MedicaoCartografica';
import { Mapa } from '@/map/Mapa';
const CHAVE_RECOLHIDA = 'dfp.agro.barraLateral.recolhida';
function lerBarraLateral() {
    try {
        return window.localStorage.getItem(CHAVE_RECOLHIDA) === 'sim';
    }
    catch {
        return false;
    }
}
function gravarBarraLateral(recolhida) {
    try {
        window.localStorage.setItem(CHAVE_RECOLHIDA, recolhida ? 'sim' : 'nao');
    }
    catch {
        // Armazenamento indisponível: estado persiste apenas em memória.
    }
}
const MARGEM_DO_SELETOR = 8;
const DESLOCAMENTO_DO_SELETOR = 12;
/**
 * Lista de feições sobrepostas no ponto clicado. Posicionado em coordenadas do canvas (o
 * contêiner `.mapa` coincide com ele) e mantido dentro da área visível do mapa.
 */
function SeletorContextual({ consulta, aoEscolher, aoFechar }) {
    const { t } = useI18n();
    const raiz = useRef(null);
    const [posicao, setPosicao] = useState(null);
    useLayoutEffect(() => {
        const elemento = raiz.current;
        const area = elemento?.parentElement;
        if (elemento === null || area === null || area === undefined) {
            return undefined;
        }
        const posicionar = () => {
            const largura = elemento.offsetWidth;
            const altura = elemento.offsetHeight;
            const ajustar = (inicio, tamanho, limite) => {
                let valor = inicio + DESLOCAMENTO_DO_SELETOR;
                if (valor + tamanho > limite - MARGEM_DO_SELETOR) {
                    valor = inicio - DESLOCAMENTO_DO_SELETOR - tamanho;
                }
                return Math.max(MARGEM_DO_SELETOR, Math.min(valor, limite - tamanho - MARGEM_DO_SELETOR));
            };
            setPosicao({
                left: ajustar(consulta.x, largura, area.clientWidth),
                top: ajustar(consulta.y, altura, area.clientHeight),
            });
        };
        posicionar();
        window.addEventListener('resize', posicionar);
        return () => window.removeEventListener('resize', posicionar);
    }, [consulta]);
    useEffect(() => {
        raiz.current?.querySelector('.seletor-contextual__opcao')?.focus();
    }, [consulta]);
    useEffect(() => {
        const aoPressionar = (evento) => {
            if (evento.key === 'Escape') {
                aoFechar();
            }
        };
        document.addEventListener('keydown', aoPressionar);
        return () => document.removeEventListener('keydown', aoPressionar);
    }, [aoFechar]);
    return (_jsxs("div", { ref: raiz, className: "seletor-contextual", role: "dialog", "aria-label": t('seletorContextual.titulo'), style: {
            left: posicao?.left ?? 0,
            top: posicao?.top ?? 0,
            visibility: posicao === null ? 'hidden' : 'visible',
        }, onClick: (evento) => evento.stopPropagation(), onMouseDown: (evento) => evento.stopPropagation(), onDoubleClick: (evento) => evento.stopPropagation(), onWheel: (evento) => evento.stopPropagation(), children: [_jsxs("div", { className: "seletor-contextual__cabecalho", children: [_jsxs("div", { children: [_jsx("h3", { className: "seletor-contextual__titulo", children: t('seletorContextual.titulo') }), _jsx("p", { className: "seletor-contextual__subtitulo", children: t('seletorContextual.subtitulo') })] }), _jsx("button", { type: "button", className: "seletor-contextual__fechar", "aria-label": t('seletorContextual.fechar'), onClick: aoFechar, children: "\u00D7" })] }), _jsx("ul", { className: "seletor-contextual__lista", children: consulta.candidatas.map((candidata) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "seletor-contextual__opcao", onClick: () => aoEscolher(candidata), children: [_jsx("span", { className: "seletor-contextual__nome", children: candidata.nome ?? t('feicao.rotuloPadrao', { id: String(candidata.feicaoId) }) }), _jsxs("span", { className: "seletor-contextual__detalhe", children: [candidata.nomeDaCamada, " \u00B7 #", candidata.feicaoId] })] }) }, `${candidata.camadaId}:${candidata.feicaoId}`))) })] }));
}
function ConteudoDoViewer({ diagnostico, aoFecharDiagnostico, }) {
    const { selecionar } = useSelecao();
    const { irParaInspecao } = useExploracao();
    const [consultaContextual, setConsultaContextual] = useState(null);
    const fecharSeletor = useCallback(() => setConsultaContextual(null), []);
    // Modo de medição: único ponto de coordenação entre os controles, a ferramenta e a seleção.
    const [modoMedicao, setModoMedicao] = useState('nenhum');
    const alternarMedicao = (modo) => setModoMedicao((atual) => (atual === modo ? 'nenhum' : modo));
    /**
     * Clique no mapa: apenas seleciona a feição e abre a inspeção, sem mover a
     * câmera. A câmera permanece onde o usuário estava.
     */
    const aoClicarNaFeicao = (camadaId, feicaoId) => {
        irParaInspecao(camadaId);
        // Map click: NÃO enquadrar. Default do provider é `enquadrar=false`.
        void selecionar(camadaId, feicaoId);
    };
    const aoEscolherCandidata = (candidata) => {
        setConsultaContextual(null);
        aoClicarNaFeicao(candidata.camadaId, candidata.feicaoId);
    };
    return (_jsx("div", { className: "palco", children: _jsxs(Mapa, { children: [_jsx(CamadasNoMapa, { aoClicarNaFeicao: aoClicarNaFeicao, aoEncontrarSobreposicao: setConsultaContextual, aoLimparSobreposicao: fecharSeletor, medicaoAtiva: modoMedicao !== 'nenhum' }), _jsx(ControlesCartograficos, { modoMedicao: modoMedicao, aoAlternarMedicao: alternarMedicao }), _jsx(MedicaoCartografica, { modo: modoMedicao, aoMudarModo: setModoMedicao }), _jsxs("div", { className: "mapa__legendas", children: [_jsx(LegendaHipsometrica, {}), _jsx(LegendaCartografica, {})] }), consultaContextual === null ? null : (_jsx(SeletorContextual, { consulta: consultaContextual, aoEscolher: aoEscolherCandidata, aoFechar: fecharSeletor })), _jsx(PainelDeExploracao, {}), diagnostico ? _jsx(GavetaTecnica, { aoFechar: aoFecharDiagnostico }) : null] }) }));
}
function AcoesDoTopoViewer({ aoAlternarDiagnostico, diagnosticoAberto, }) {
    const { t } = useI18n();
    return (_jsxs(_Fragment, { children: [_jsx(Saude, {}), _jsx("button", { type: "button", className: "botao-icone", "aria-expanded": diagnosticoAberto, "aria-label": t('diagnostico.titulo'), title: t('diagnostico.titulo'), onClick: aoAlternarDiagnostico, children: _jsx(IconeDiagnostico, {}) })] }));
}
/** Composição do Viewer: sessão técnica em memória, inspeção, catálogo, seleção e o mapa. */
function Viewer({ rota, usuario, barraLateralRecolhida, aoAlternarBarraLateral, aoNavegar, aoSair, }) {
    // Estado único da gaveta técnica —> consumido pelo cabeçalho (toggle) e pela
    // ConteudoDoViewer (renderização e fechamento). O Cabeçalho alterna; a
    // gaveta apenas consome o callback de fechamento.
    const [diagnostico, setDiagnostico] = useState(false);
    return (_jsx(InspecaoProvider, { children: _jsx(SessaoProvider, { children: _jsx(CamadasProvider, { children: _jsx(SelecaoProvider, { children: _jsx(ExploracaoProvider, { children: _jsx(AppShell, { rota: rota, usuario: usuario, barraLateralRecolhida: barraLateralRecolhida, aoAlternarBarraLateral: aoAlternarBarraLateral, aoNavegar: aoNavegar, aoSair: aoSair, acoesTopo: _jsx(AcoesDoTopoViewer, { aoAlternarDiagnostico: () => setDiagnostico((valor) => !valor), diagnosticoAberto: diagnostico }), children: _jsx(ConteudoDoViewer, { diagnostico: diagnostico, aoFecharDiagnostico: () => setDiagnostico(false) }) }) }) }) }) }) }));
}
/** Sem sessão administrativa: `/ingestao` redireciona para `/`, que exibe o Login. */
function SemSessaoAdmin({ rota, navegarPara, }) {
    useEffect(() => {
        if (rota === '/ingestao') {
            navegarPara('/');
        }
    }, [rota, navegarPara]);
    if (rota === '/ingestao') {
        return _jsx("div", { className: "pagina-login", "aria-busy": "true" });
    }
    return _jsx(Login, {});
}
function PaginasAdministrativas() {
    const [rota, navegarPara] = useRota();
    const { sessao, sair } = useSessaoAdmin();
    const [barraLateralRecolhida, setBarraLateralRecolhida] = useState(lerBarraLateral);
    const aoAlternarBarraLateral = () => {
        setBarraLateralRecolhida((valor) => {
            const proximo = !valor;
            gravarBarraLateral(proximo);
            return proximo;
        });
    };
    // Sessão ainda sendo restaurada: evita piscar a tela de login antes de `/auth/me` responder.
    if (sessao === undefined) {
        return _jsx("div", { className: "pagina-login", "aria-busy": "true" });
    }
    if (sessao === null) {
        return _jsx(SemSessaoAdmin, { rota: rota, navegarPara: navegarPara });
    }
    const usuario = sessao.username;
    const propsDaCasca = {
        rota,
        usuario,
        barraLateralRecolhida,
        aoAlternarBarraLateral,
        aoNavegar: navegarPara,
        aoSair: sair,
    };
    if (rota === '/viewer') {
        return _jsx(Viewer, { ...propsDaCasca });
    }
    if (rota === '/ingestao') {
        return (_jsx(AppShell, { ...propsDaCasca, children: _jsx(PaginaIngestao, {}) }));
    }
    return (_jsx(AppShell, { ...propsDaCasca, children: _jsx(HomeOperacional, { navegarPara: navegarPara }) }));
}
/** Composição da aplicação: sessão administrativa e roteamento mínimo entre as três páginas. */
export function App() {
    return (_jsx(I18nProvider, { children: _jsx(SessaoAdminProvider, { children: _jsx(PaginasAdministrativas, {}) }) }));
}
